import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../api/client';
import PhoneVerificationModal from '../components/phone-verification-modal';
import { FaceVerifyScreens } from '../constants/face.const';
import { useApp } from '../context/usePortal';

type ApiError = Error & {
  response?: { data?: { error?: { code?: string; message?: string } } };
};

/**
 * Saving a security phrase needs a one-time code sent to the user's phone.
 * `requestSave(phrase)` opens that verification step; once the code checks out
 * the phrase is saved and `onSaved` runs. Render `otpModal` once in the caller.
 */
export function useSaveSecurityPhrase({ onSaved }: { onSaved: () => void }) {
  const [pendingPhrase, setPendingPhrase] = useState<string | null>(null);
  const { user } = useApp();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    void,
    ApiError,
    { securityPhrase: string; otp: string }
  >({
    mutationFn: async ({ securityPhrase, otp }) => {
      await apiClient.post('/users/security-phrase', { otp, securityPhrase });
    },
    onSuccess: () => {
      toast.success('Security phrase saved');
      setPendingPhrase(null);
      queryClient.invalidateQueries({ queryKey: ['getLastUpdates'] });
      onSaved();
    },
    onError: (error: ApiError) => {
      const apiError = error?.response?.data?.error;
      // The OTP modal shows its own lockout message for repeated failures.
      if (apiError?.code === 'OTP_FAILED_ATTEMPTS') return;
      toast.error(
        apiError?.message || 'Failed to save security phrase. Please try again.'
      );
    }
  });

  const otpModal = (
    <PhoneVerificationModal
      phoneNumber={`${user?.country_code}-${user?.user_phone_number}`}
      onClose={() => setPendingPhrase(null)}
      onVerify={(otp) => {
        if (pendingPhrase)
          mutation.mutate({ securityPhrase: pendingPhrase, otp });
      }}
      isOpen={pendingPhrase !== null}
      verifyError={mutation.error}
      screen={FaceVerifyScreens.RESET_PIN}
      disableCannotAccessOldPhone={true}
      isVerifying={mutation.isPending}
    />
  );

  return {
    requestSave: (phrase: string) => setPendingPhrase(phrase),
    isSaving: mutation.isPending,
    otpModal
  };
}
